import { View } from 'web-mojo';
import { ServiceRecordList } from '@portal/models/ServiceRecord.js';

export default class MachineServiceHistoryPanel extends View {
    constructor({ machine }) {
        super();
        this.machine = machine;
        this.allRecords = [];
        this.expanded = new Set();

        // filter state
        this.filterDateFrom = '';
        this.filterDateTo = '';
        this.filterOperator = '';
        this.filterActivity = '';
    }

    async onInit() {
        await this.loadRecords();
    }

    async loadRecords() {
        const response = await app.api.get('/api/routes/service_record', {
            machine: this.machine.get('id'),
            size: 500,
            sort: '-checked_in_at',
        });
        if (!response.success || !response.data.status) {
            app.toast.error('Failed to load service history');
            return;
        }
        this.allRecords = response.data.data || [];
        this.render();
    }

    get filteredRecords() {
        return this.allRecords.filter(r => {
            if (this.filterDateFrom && r.checked_in_at) {
                if (r.checked_in_at.slice(0, 10) < this.filterDateFrom) return false;
            }
            if (this.filterDateTo && r.checked_in_at) {
                if (r.checked_in_at.slice(0, 10) > this.filterDateTo) return false;
            }
            if (this.filterOperator) {
                const opId = String(r.operator?.id || '');
                if (opId !== this.filterOperator) return false;
            }
            if (this.filterActivity) {
                if ((r.activity_type || '') !== this.filterActivity) return false;
            }
            return true;
        });
    }

    get uniqueOperators() {
        const seen = new Map();
        for (const r of this.allRecords) {
            if (r.operator?.id) {
                seen.set(String(r.operator.id), r.operator.display_name || r.operator.user?.username || r.operator.id);
            }
        }
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }

    get uniqueActivityTypes() {
        return [...new Set(this.allRecords.map(r => r.activity_type).filter(Boolean))];
    }

    onActionApplyFilter() {
        this.filterDateFrom = this.element.querySelector('[name=filter_date_from]')?.value || '';
        this.filterDateTo = this.element.querySelector('[name=filter_date_to]')?.value || '';
        this.filterOperator = this.element.querySelector('[name=filter_operator]')?.value || '';
        this.filterActivity = this.element.querySelector('[name=filter_activity]')?.value || '';
        this.render();
    }

    onActionClearFilter() {
        this.filterDateFrom = '';
        this.filterDateTo = '';
        this.filterOperator = '';
        this.filterActivity = '';
        this.render();
    }

    onActionExport() {
        const records = this.filteredRecords;
        if (records.length === 0) {
            app.toast.warning('No records to export');
            return;
        }
        const rows = records.map(r => ({
            date: r.checked_in_at?.slice(0, 10) || '',
            time_in: this.formatTime(r.checked_in_at),
            time_out: this.formatTime(r.completed_at),
            operator: r.operator?.display_name || r.operator?.user?.username || '',
            activity_type: r.activity_type || '',
            duration_min: r.actual_service_duration || '',
            notes: r.notes || '',
            parts_used: (r.parts_used || []).map(p => `${p.sku}×${p.qty}`).join('; '),
            collection_amount: r.collection_amount || '',
            photos: (r.photo_urls || []).length,
        }));
        const headers = Object.keys(rows[0]);
        const lines = [
            headers.join(','),
            ...rows.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `service_history_${this.machine.get('name')}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    onActionToggle(event, el) {
        const id = el.getAttribute('data-id');
        if (this.expanded.has(id)) {
            this.expanded.delete(id);
        } else {
            this.expanded.add(id);
        }
        this.render();
    }

    formatDate(iso) {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    formatTime(iso) {
        if (!iso) return '';
        return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }

    set template(v) { this._t = v; }

    get template() {
        const machineName = this.machine.get('name');
        const records = this.filteredRecords;
        const hasFilters = this.filterDateFrom || this.filterDateTo || this.filterOperator || this.filterActivity;

        const operatorOptions = this.uniqueOperators.map(op =>
            `<option value="${op.id}" ${this.filterOperator === op.id ? 'selected' : ''}>${op.name}</option>`
        ).join('');

        const activityOptions = this.uniqueActivityTypes.map(a =>
            `<option value="${a}" ${this.filterActivity === a ? 'selected' : ''}>${a.charAt(0).toUpperCase() + a.slice(1)}</option>`
        ).join('');

        const filterBar = `
            <div class="px-3 py-2 border-bottom bg-light">
                <div class="d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label class="form-label small mb-1">From</label>
                        <input type="date" class="form-control form-control-sm" name="filter_date_from"
                            value="${this.filterDateFrom}" style="width:130px;">
                    </div>
                    <div>
                        <label class="form-label small mb-1">To</label>
                        <input type="date" class="form-control form-control-sm" name="filter_date_to"
                            value="${this.filterDateTo}" style="width:130px;">
                    </div>
                    <div>
                        <label class="form-label small mb-1">Operator</label>
                        <select class="form-select form-select-sm" name="filter_operator" style="width:140px;">
                            <option value="">All</option>
                            ${operatorOptions}
                        </select>
                    </div>
                    <div>
                        <label class="form-label small mb-1">Activity</label>
                        <select class="form-select form-select-sm" name="filter_activity" style="width:130px;">
                            <option value="">All</option>
                            ${activityOptions}
                        </select>
                    </div>
                    <button type="button" class="btn btn-sm btn-primary" data-action="apply-filter">Apply</button>
                    ${hasFilters ? `<button type="button" class="btn btn-sm btn-outline-secondary" data-action="clear-filter">Clear</button>` : ''}
                    <button type="button" class="btn btn-sm btn-outline-secondary ms-auto" data-action="export">
                        <i class="bi bi-download me-1"></i>Export CSV
                    </button>
                </div>
            </div>
        `;

        if (this.allRecords.length === 0) {
            return `
                ${filterBar}
                <div class="p-4 text-center text-muted">
                    <i class="bi bi-clock-history fs-2 d-block mb-2"></i>
                    No service history for ${machineName} yet.
                </div>
            `;
        }

        if (records.length === 0) {
            return `
                ${filterBar}
                <div class="p-4 text-center text-muted">
                    <i class="bi bi-funnel fs-2 d-block mb-2"></i>
                    No records match the current filters.
                </div>
            `;
        }

        const rows = records.map(r => {
            const id = String(r.id);
            const isExpanded = this.expanded.has(id);
            const date = this.formatDate(r.checked_in_at);
            const timeIn = this.formatTime(r.checked_in_at);
            const timeOut = this.formatTime(r.completed_at);
            const operator = r.operator?.display_name || r.operator?.user?.username || '—';
            const activity = r.activity_type || '—';
            const duration = r.actual_service_duration ? `${r.actual_service_duration} min` : '—';
            const notes = r.notes || '';
            const parts = (r.parts_used || []).map(p => `${p.sku} ×${p.qty}`).join(', ') || 'None';
            const photoCount = (r.photo_urls || []).length;

            return `
                <div class="border-bottom">
                    <div class="d-flex align-items-start justify-content-between px-4 py-3">
                        <div>
                            <div class="fw-semibold small">${date}</div>
                            <div class="text-muted small">${operator} · ${activity}
                                ${timeIn && timeOut ? ` · ${timeIn}–${timeOut}` : ''}
                                ${duration !== '—' ? ` · ${duration}` : ''}
                            </div>
                            ${notes && !isExpanded ? `<div class="text-muted small mt-1 fst-italic">"${notes.substring(0, 80)}${notes.length > 80 ? '…' : ''}"</div>` : ''}
                        </div>
                        <button type="button" class="btn btn-sm btn-link text-muted p-0 ms-3"
                            data-action="toggle" data-id="${id}">
                            <i class="bi bi-chevron-${isExpanded ? 'up' : 'down'}"></i>
                        </button>
                    </div>
                    ${isExpanded ? `
                        <div class="px-4 pb-3 bg-light small">
                            <div class="row g-2">
                                <div class="col-6">
                                    <span class="text-muted">Parts used:</span> ${parts}
                                </div>
                                <div class="col-6">
                                    <span class="text-muted">Photos:</span> ${photoCount}
                                </div>
                                ${notes ? `<div class="col-12"><span class="text-muted">Notes:</span> ${notes}</div>` : ''}
                                ${r.collection_amount ? `<div class="col-6"><span class="text-muted">Collection:</span> ${r.collection_amount} ${r.collection_currency || ''}</div>` : ''}
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;
        }).join('');

        return `
            ${filterBar}
            <div>
                <div class="px-4 py-2 bg-light border-bottom d-flex gap-2 small text-muted">
                    ${records.length} record${records.length !== 1 ? 's' : ''}${hasFilters ? ' (filtered)' : ''}
                    ${hasFilters ? `of ${this.allRecords.length} total` : ''}
                </div>
                ${rows}
            </div>
        `;
    }
}
